import { Box } from '@chakra-ui/react';
import type { LucideIcon } from 'lucide-react';

/** Icon-only status toggle — the single button style across the whole
 *  status bar: the glyph stays neutral gray in both states; when ON the
 *  background lightens via the shared `bg.active` token and the glyph
 *  darkens to `text.primary` instead of coloring the icon. All colors
 *  are semantic tokens so the button reads correctly in both light and
 *  dark color modes. The action's name lives in the hover tooltip.
 *  `dimmed` renders the button at reduced opacity (e.g. per-type snap
 *  switches while the master OSNAP switch is off) without disabling it.
 *  `label` renders a mono text face after the glyph (Coords readout,
 *  OCS text pills); `onContextMenu` lets pills host their popup menus. */
export function IconToggle({ icon: Icon, on, onClick, title, dimmed = false, label, onContextMenu, active }: {
    icon?: LucideIcon;
    on: boolean;
    onClick?: () => void;
    title: string;
    dimmed?: boolean;
    label?: string;
    onContextMenu?: (e: React.MouseEvent) => void;
    /** Highlights the pill (e.g. a popup open on it) without implying on. */
    active?: boolean;
}) {
    return (
        <Box
            as="button"
            px={2}
            py={0.5}
            borderRadius="sm"
            border="1px solid"
            borderColor={active ? 'border.hover' : 'border.base'}
            bg={on || active ? 'bg.active' : 'transparent'}
            color={on || active ? 'text.primary' : 'icon.base'}
            opacity={dimmed ? 0.45 : 1}
            cursor="pointer"
            userSelect="none"
            display="inline-flex"
            alignItems="center"
            gap={1}
            flexShrink={0}
            whiteSpace="nowrap"
            transition="background 120ms ease, border-color 120ms ease, color 120ms ease, opacity 120ms ease"
            _hover={{ borderColor: 'border.hover' }}
            onClick={onClick}
            onContextMenu={onContextMenu}
            title={title}
            aria-label={title}
        >
            {Icon && <Icon size={13} />}
            {label && (
                <Box as="span" fontFamily="mono" fontSize="10px" lineHeight="1">
                    {label}
                </Box>
            )}
        </Box>
    );
}
